import * as i0 from '@angular/core';
import { Directive, Input, InjectionToken, HostBinding, Optional, Inject, ChangeDetectionStrategy, Component, NgModule } from '@angular/core';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { CommonModule } from '@angular/common';

/** A directive for passing the bottom image into small stage component. */
const _c0 = [[["", "nxSmallStageHeader", ""]], [["", "nxSmallStageImageStart", ""]], "*", [["", "nxSmallStageImageEnd", ""]], [["", "nxSmallStageImageBottom", ""]]];
const _c1 = ["[nxSmallStageHeader]", "[nxSmallStageImageStart]", "*", "[nxSmallStageImageEnd]", "[nxSmallStageImageBottom]"];
class NxSmallStageImageBottomDirective {
  static {
    this.ɵfac = function NxSmallStageImageBottomDirective_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || NxSmallStageImageBottomDirective)();
    };
  }
  static {
    this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
      type: NxSmallStageImageBottomDirective,
      selectors: [["", "nxSmallStageImageBottom", ""]]
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(NxSmallStageImageBottomDirective, [{
    type: Directive,
    args: [{
      selector: '[nxSmallStageImageBottom]',
      standalone: true
    }]
  }], null, null);
})();

/** A directive for passing the end image into small stage component. */
class NxSmallStageImageEndDirective {
  static {
    this.ɵfac = function NxSmallStageImageEndDirective_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || NxSmallStageImageEndDirective)();
    };
  }
  static {
    this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
      type: NxSmallStageImageEndDirective,
      selectors: [["", "nxSmallStageImageEnd", ""]]
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(NxSmallStageImageEndDirective, [{
    type: Directive,
    args: [{
      selector: '[nxSmallStageImageEnd]',
      standalone: true
    }]
  }], null, null);
})();

/** A directive for passing contents to small stage header. */
class NxSmallStageHeaderDirective {
  static {
    this.ɵfac = function NxSmallStageHeaderDirective_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || NxSmallStageHeaderDirective)();
    };
  }
  static {
    this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
      type: NxSmallStageHeaderDirective,
      selectors: [["", "nxSmallStageHeader", ""]]
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(NxSmallStageHeaderDirective, [{
    type: Directive,
    args: [{
      selector: '[nxSmallStageHeader]',
      standalone: true
    }]
  }], null, null);
})();

/**
 * This is an image that can be placed into the small stage component.
 */
class NxSmallStageImageDirective {
  /**
   * The source url of the image.
   */
  set src(value) {
    this._renderer.setStyle(this._elementRef.nativeElement, 'backgroundImage', `url('${value}')`);
  }
  constructor(_elementRef, _renderer) {
    this._elementRef = _elementRef;
    this._renderer = _renderer;
  }
  static {
    this.ɵfac = function NxSmallStageImageDirective_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || NxSmallStageImageDirective)(i0.ɵɵdirectiveInject(i0.ElementRef), i0.ɵɵdirectiveInject(i0.Renderer2));
    };
  }
  static {
    this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
      type: NxSmallStageImageDirective,
      selectors: [["nx-small-stage-image"]],
      inputs: {
        src: "src"
      }
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(NxSmallStageImageDirective, [{
    type: Directive,
    args: [{
      selector: 'nx-small-stage-image',
      standalone: true
    }]
  }], () => [{
    type: i0.ElementRef
  }, {
    type: i0.Renderer2
  }], {
    src: [{
      type: Input
    }]
  });
})();

/** A directive for passing the start image into small stage component. */
class NxSmallStageImageStartDirective {
  static {
    this.ɵfac = function NxSmallStageImageStartDirective_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || NxSmallStageImageStartDirective)();
    };
  }
  static {
    this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
      type: NxSmallStageImageStartDirective,
      selectors: [["", "nxSmallStageImageStart", ""]]
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(NxSmallStageImageStartDirective, [{
    type: Directive,
    args: [{
      selector: '[nxSmallStageImageStart]',
      standalone: true
    }]
  }], null, null);
})();
const SMALL_STAGE_DEFAULT_OPTIONS = new InjectionToken('SMALL_STAGE_DEFAULT_OPTIONS');
class NxSmallStageComponent {
  /**
   * **Expert option**
   *
   * Sets the appearance of the small stage.
   *
   * Default: `'default'`.
   */
  set appearance(value) {
    if (this._appearance !== value) {
      this._appearance = value;
    }
  }
  get appearance() {
    return this._appearance || this._defaultOptions?.appearance || 'default';
  }
  /**
   * Reduces the width of the text to 6/12 instead of 8/12.
   *
   * **Only works with appearance = 'expert'.**.
   */
  set narrow(value) {
    this._narrow = coerceBooleanProperty(value);
  }
  get narrow() {
    return this._narrow;
  }
  get _isExpert() {
    return this.appearance === 'expert';
  }
  constructor(_defaultOptions) {
    this._defaultOptions = _defaultOptions;
    this._narrow = false;
  }
  static {
    this.ɵfac = function NxSmallStageComponent_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || NxSmallStageComponent)(i0.ɵɵdirectiveInject(SMALL_STAGE_DEFAULT_OPTIONS, 8));
    };
  }
  static {
    this.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
      type: NxSmallStageComponent,
      selectors: [["nx-small-stage"]],
      hostVars: 4,
      hostBindings: function NxSmallStageComponent_HostBindings(rf, ctx) {
        if (rf & 2) {
          i0.ɵɵclassProp("is-narrow", ctx.narrow)("is-expert", ctx._isExpert);
        }
      },
      inputs: {
        appearance: "appearance",
        narrow: "narrow"
      },
      ngContentSelectors: _c1,
      decls: 11,
      vars: 0,
      consts: [[1, "container"], [1, "header"], [1, "image-start"], [1, "content"], [1, "image-end"], [1, "image-bottom"]],
      template: function NxSmallStageComponent_Template(rf, ctx) {
        if (rf & 1) {
          i0.ɵɵprojectionDef(_c0);
          i0.ɵɵelementStart(0, "div", 0)(1, "div", 1);
          i0.ɵɵprojection(2);
          i0.ɵɵelementEnd();
          i0.ɵɵelementStart(3, "div", 2);
          i0.ɵɵprojection(4, 1);
          i0.ɵɵelementEnd();
          i0.ɵɵelementStart(5, "div", 3);
          i0.ɵɵprojection(6, 2);
          i0.ɵɵelementEnd();
          i0.ɵɵelementStart(7, "div", 4);
          i0.ɵɵprojection(8, 3);
          i0.ɵɵelementEnd();
          i0.ɵɵelementStart(9, "div", 5);
          i0.ɵɵprojection(10, 4);
          i0.ɵɵelementEnd()();
        }
      },
      styles: ["[_nghost-%COMP%]{display:flex;color:var(--small-stage-text-color);background-color:var(--small-stage-default-background-color);justify-content:center}.container[_ngcontent-%COMP%]{display:-ms-grid;display:grid;-ms-grid-columns:2fr 8fr 2fr;grid-template-columns:2fr 8fr 2fr;column-gap:32px;padding:0 32px;flex:1 1 auto;width:100%}.content[_ngcontent-%COMP%]{-ms-grid-row:2;grid-row:2;flex:1 1 auto}.image-start[_ngcontent-%COMP%], .image-end[_ngcontent-%COMP%]{-ms-grid-row:2;grid-row:2}.image-start[_ngcontent-%COMP%], .image-end[_ngcontent-%COMP%], .image-bottom[_ngcontent-%COMP%]{display:flex;align-items:flex-end}  nx-small-stage-image{display:block;width:100%;height:100%;min-height:var(--small-stage-image-min-height);max-height:var(--small-stage-image-max-height);background-size:contain;background-repeat:no-repeat}  [nxSmallStageImageEnd]{background-position:bottom right}  [nxSmallStageImageBottom]{background-position:bottom center;height:var(--small-stage-image-bottom-height)}  [nxSmallStageImageStart]{background-position:bottom left}.header[_ngcontent-%COMP%]{-ms-grid-column:1;-ms-grid-column-span:3;grid-column:1/-1;min-height:var(--small-stage-header-height);padding:var(--small-stage-header-padding);display:flex}@media (max-width: 991px){.header[_ngcontent-%COMP%]{min-height:var(--small-stage-header-height-tablet)}.header[_ngcontent-%COMP%]:empty{padding:0}}@media (max-width: 703px){.header[_ngcontent-%COMP%]{min-height:var(--small-stage-header-height-mobile)}}[_nghost-%COMP%]:not(.is-expert)   .container[_ngcontent-%COMP%]{max-width:var(--grid-max-width)}[_nghost-%COMP%]:not(.is-expert)   .content[_ngcontent-%COMP%]{-ms-grid-column:2;grid-column:2;text-align:center;margin-bottom:var(--small-stage-content-margin-bottom)}[_nghost-%COMP%]:not(.is-expert)   .image-start[_ngcontent-%COMP%]{-ms-grid-column:1;grid-column:1}[_nghost-%COMP%]:not(.is-expert)   .image-end[_ngcontent-%COMP%]{-ms-grid-column:3;grid-column:3}[_nghost-%COMP%]:not(.is-expert)   .image-bottom[_ngcontent-%COMP%]{display:none;-ms-grid-row:3;grid-row:3}@media (min-width: 704px){[_nghost-%COMP%]:not(.is-expert)   .container[_ngcontent-%COMP%]{min-height:var(--small-stage-min-height)}}@media (max-width: 991px){[_nghost-%COMP%]:not(.is-expert)   .container[_ngcontent-%COMP%]{-ms-grid-columns:1fr;grid-template-columns:1fr}[_nghost-%COMP%]:not(.is-expert)   .image-start[_ngcontent-%COMP%], [_nghost-%COMP%]:not(.is-expert)   .image-end[_ngcontent-%COMP%]{display:none}[_nghost-%COMP%]:not(.is-expert)   .content[_ngcontent-%COMP%]{-ms-grid-column:1;grid-column:1;margin-bottom:var(--small-stage-content-margin-bottom)}[_nghost-%COMP%]:not(.is-expert)   .image-bottom[_ngcontent-%COMP%]{display:block}[_nghost-%COMP%]:not(.is-expert)     [nxSmallStageImageBottom]{margin-top:calc(var(--small-stage-image-bottom-margin-top) - var(--small-stage-content-margin-bottom))}}@media (max-width: 703px){[_nghost-%COMP%]:not(.is-expert)   .content[_ngcontent-%COMP%]{margin-bottom:var(--small-stage-mobile-content-margin-bottom)}[_nghost-%COMP%]:not(.is-expert)   .header[_ngcontent-%COMP%]{min-height:var(--small-stage-mobile-header-height);padding:0}[_nghost-%COMP%]:not(.is-expert)   .header[_ngcontent-%COMP%]:not(:empty){min-height:auto;padding:var(--small-stage-mobile-header-padding)}[_nghost-%COMP%]:not(.is-expert)     [nxSmallStageImageBottom]{margin-top:calc(var(--small-stage-image-bottom-margin-top) - var(--small-stage-mobile-content-margin-bottom))}}.is-expert[_nghost-%COMP%]   .container[_ngcontent-%COMP%]{-ms-grid-columns:8fr 3fr 1fr;grid-template-columns:8fr 3fr 1fr;-ms-grid-rows:auto 1fr;grid-template-rows:auto 1fr}.is-expert[_nghost-%COMP%]   .image-start[_ngcontent-%COMP%]{display:none}.is-expert[_nghost-%COMP%]   .image-end[_ngcontent-%COMP%]{-ms-grid-column:2;grid-column:2}[dir=rtl]   .is-expert[_nghost-%COMP%]     [nxSmallStageImageEnd]{background-position:bottom left}.is-expert[_nghost-%COMP%]   .content[_ngcontent-%COMP%]{-ms-grid-column:1;grid-column:1;margin-top:var(--small-stage-expert-content-margin-top);margin-bottom:var(--small-stage-expert-content-margin-bottom);display:flex;flex-direction:column;justify-content:flex-end}.is-expert[_nghost-%COMP%]   .header[_ngcontent-%COMP%]{min-height:var(--small-stage-expert-header-height);padding:var(--small-stage-expert-header-padding)}@media (min-width: 704px){.is-expert[_nghost-%COMP%]   .content[_ngcontent-%COMP%]{min-height:var(--small-stage-expert-content-min-height)}}@media (max-width: 991px){.is-expert[_nghost-%COMP%]   .container[_ngcontent-%COMP%]{-ms-grid-columns:8fr 4fr;grid-template-columns:8fr 4fr}.is-expert[_nghost-%COMP%]   .content[_ngcontent-%COMP%]{margin-top:0;margin-bottom:var(--small-stage-expert-tablet-content-margin-bottom)}.is-expert[_nghost-%COMP%]     [nxSmallStageImageEnd]{background-position:bottom center}}@media (max-width: 450px){.is-expert[_nghost-%COMP%]   .container[_ngcontent-%COMP%]{-ms-grid-columns:1fr;grid-template-columns:1fr}.is-expert[_nghost-%COMP%]   .header[_ngcontent-%COMP%]{padding:var(--small-stage-expert-mobile-header-padding)}.is-expert[_nghost-%COMP%]   .header[_ngcontent-%COMP%]:empty{padding:0}.is-expert[_nghost-%COMP%]   .image-end[_ngcontent-%COMP%]{-ms-grid-row:3;grid-row:3;-ms-grid-column:1;grid-column:1;margin:0;height:var(--small-stage-expert-image-min-height);min-height:var(--small-stage-image-min-height)}.is-expert[_nghost-%COMP%]     [nxSmallStageImageEnd]{background-position:bottom right}}@media (min-width: 992px){.is-expert.is-narrow[_nghost-%COMP%]   .container[_ngcontent-%COMP%]{-ms-grid-columns:6fr 5fr 1fr;grid-template-columns:6fr 5fr 1fr}}@media (max-width: 703px){.container[_ngcontent-%COMP%]{padding:0 16px;column-gap:16px}}"],
      changeDetection: 0
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(NxSmallStageComponent, [{
    type: Component,
    args: [{
      selector: 'nx-small-stage',
      changeDetection: ChangeDetectionStrategy.OnPush,
      standalone: true,
      template: "<div class=\"container\">\n    <div class=\"header\">\n        <ng-content select=\"[nxSmallStageHeader]\"></ng-content>\n    </div>\n    <div class=\"image-start\">\n        <ng-content select=\"[nxSmallStageImageStart]\"></ng-content>\n    </div>\n    <div class=\"content\">\n        <ng-content></ng-content>\n    </div>\n    <div class=\"image-end\">\n        <ng-content select=\"[nxSmallStageImageEnd]\"></ng-content>\n    </div>\n    <div class=\"image-bottom\">\n        <ng-content select=\"[nxSmallStageImageBottom]\"></ng-content>\n    </div>\n</div>\n",
      styles: [":host{display:flex;color:var(--small-stage-text-color);background-color:var(--small-stage-default-background-color);justify-content:center}.container{display:-ms-grid;display:grid;-ms-grid-columns:2fr 8fr 2fr;grid-template-columns:2fr 8fr 2fr;column-gap:32px;padding:0 32px;flex:1 1 auto;width:100%}.content{-ms-grid-row:2;grid-row:2;flex:1 1 auto}.image-start,.image-end{-ms-grid-row:2;grid-row:2}.image-start,.image-end,.image-bottom{display:flex;align-items:flex-end}::ng-deep nx-small-stage-image{display:block;width:100%;height:100%;min-height:var(--small-stage-image-min-height);max-height:var(--small-stage-image-max-height);background-size:contain;background-repeat:no-repeat}::ng-deep [nxSmallStageImageEnd]{background-position:bottom right}::ng-deep [nxSmallStageImageBottom]{background-position:bottom center;height:var(--small-stage-image-bottom-height)}::ng-deep [nxSmallStageImageStart]{background-position:bottom left}.header{-ms-grid-column:1;-ms-grid-column-span:3;grid-column:1/-1;min-height:var(--small-stage-header-height);padding:var(--small-stage-header-padding);display:flex}@media (max-width: 991px){.header{min-height:var(--small-stage-header-height-tablet)}.header:empty{padding:0}}@media (max-width: 703px){.header{min-height:var(--small-stage-header-height-mobile)}}:host(:not(.is-expert)) .container{max-width:var(--grid-max-width)}:host(:not(.is-expert)) .content{-ms-grid-column:2;grid-column:2;text-align:center;margin-bottom:var(--small-stage-content-margin-bottom)}:host(:not(.is-expert)) .image-start{-ms-grid-column:1;grid-column:1}:host(:not(.is-expert)) .image-end{-ms-grid-column:3;grid-column:3}:host(:not(.is-expert)) .image-bottom{display:none;-ms-grid-row:3;grid-row:3}@media (min-width: 704px){:host(:not(.is-expert)) .container{min-height:var(--small-stage-min-height)}}@media (max-width: 991px){:host(:not(.is-expert)) .container{-ms-grid-columns:1fr;grid-template-columns:1fr}:host(:not(.is-expert)) .image-start,:host(:not(.is-expert)) .image-end{display:none}:host(:not(.is-expert)) .content{-ms-grid-column:1;grid-column:1;margin-bottom:var(--small-stage-content-margin-bottom)}:host(:not(.is-expert)) .image-bottom{display:block}:host(:not(.is-expert)) ::ng-deep [nxSmallStageImageBottom]{margin-top:calc(var(--small-stage-image-bottom-margin-top) - var(--small-stage-content-margin-bottom))}}@media (max-width: 703px){:host(:not(.is-expert)) .content{margin-bottom:var(--small-stage-mobile-content-margin-bottom)}:host(:not(.is-expert)) .header{min-height:var(--small-stage-mobile-header-height);padding:0}:host(:not(.is-expert)) .header:not(:empty){min-height:auto;padding:var(--small-stage-mobile-header-padding)}:host(:not(.is-expert)) ::ng-deep [nxSmallStageImageBottom]{margin-top:calc(var(--small-stage-image-bottom-margin-top) - var(--small-stage-mobile-content-margin-bottom))}}:host(.is-expert) .container{-ms-grid-columns:8fr 3fr 1fr;grid-template-columns:8fr 3fr 1fr;-ms-grid-rows:auto 1fr;grid-template-rows:auto 1fr}:host(.is-expert) .image-start{display:none}:host(.is-expert) .image-end{-ms-grid-column:2;grid-column:2}:host-context([dir=rtl]) :host(.is-expert) ::ng-deep [nxSmallStageImageEnd]{background-position:bottom left}:host(.is-expert) .content{-ms-grid-column:1;grid-column:1;margin-top:var(--small-stage-expert-content-margin-top);margin-bottom:var(--small-stage-expert-content-margin-bottom);display:flex;flex-direction:column;justify-content:flex-end}:host(.is-expert) .header{min-height:var(--small-stage-expert-header-height);padding:var(--small-stage-expert-header-padding)}@media (min-width: 704px){:host(.is-expert) .content{min-height:var(--small-stage-expert-content-min-height)}}@media (max-width: 991px){:host(.is-expert) .container{-ms-grid-columns:8fr 4fr;grid-template-columns:8fr 4fr}:host(.is-expert) .content{margin-top:0;margin-bottom:var(--small-stage-expert-tablet-content-margin-bottom)}:host(.is-expert) ::ng-deep [nxSmallStageImageEnd]{background-position:bottom center}}@media (max-width: 450px){:host(.is-expert) .container{-ms-grid-columns:1fr;grid-template-columns:1fr}:host(.is-expert) .header{padding:var(--small-stage-expert-mobile-header-padding)}:host(.is-expert) .header:empty{padding:0}:host(.is-expert) .image-end{-ms-grid-row:3;grid-row:3;-ms-grid-column:1;grid-column:1;margin:0;height:var(--small-stage-expert-image-min-height);min-height:var(--small-stage-image-min-height)}:host(.is-expert) ::ng-deep [nxSmallStageImageEnd]{background-position:bottom right}}@media (min-width: 992px){:host(.is-expert.is-narrow) .container{-ms-grid-columns:6fr 5fr 1fr;grid-template-columns:6fr 5fr 1fr}}@media (max-width: 703px){.container{padding:0 16px;column-gap:16px}}\n"]
    }]
  }], () => [{
    type: undefined,
    decorators: [{
      type: Optional
    }, {
      type: Inject,
      args: [SMALL_STAGE_DEFAULT_OPTIONS]
    }]
  }], {
    appearance: [{
      type: Input
    }],
    narrow: [{
      type: Input
    }, {
      type: HostBinding,
      args: ['class.is-narrow']
    }],
    _isExpert: [{
      type: HostBinding,
      args: ['class.is-expert']
    }]
  });
})();
class NxSmallStageModule {
  static {
    this.ɵfac = function NxSmallStageModule_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || NxSmallStageModule)();
    };
  }
  static {
    this.ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
      type: NxSmallStageModule,
      imports: [CommonModule, NxSmallStageImageDirective, NxSmallStageImageStartDirective, NxSmallStageImageEndDirective, NxSmallStageHeaderDirective, NxSmallStageComponent, NxSmallStageImageBottomDirective],
      exports: [NxSmallStageImageDirective, NxSmallStageImageStartDirective, NxSmallStageImageEndDirective, NxSmallStageHeaderDirective, NxSmallStageComponent, NxSmallStageImageBottomDirective]
    });
  }
  static {
    this.ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({
      imports: [CommonModule]
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(NxSmallStageModule, [{
    type: NgModule,
    args: [{
      imports: [CommonModule, NxSmallStageImageDirective, NxSmallStageImageStartDirective, NxSmallStageImageEndDirective, NxSmallStageHeaderDirective, NxSmallStageComponent, NxSmallStageImageBottomDirective],
      exports: [NxSmallStageImageDirective, NxSmallStageImageStartDirective, NxSmallStageImageEndDirective, NxSmallStageHeaderDirective, NxSmallStageComponent, NxSmallStageImageBottomDirective],
      providers: []
    }]
  }], null, null);
})();

/**
 * Generated bundle index. Do not edit.
 */

export { NxSmallStageComponent, NxSmallStageHeaderDirective, NxSmallStageImageBottomDirective, NxSmallStageImageDirective, NxSmallStageImageEndDirective, NxSmallStageImageStartDirective, NxSmallStageModule, SMALL_STAGE_DEFAULT_OPTIONS };
